import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Icon, Spinner } from '../components/ui';

interface Tier {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  limits: {
    monthly_characters: number;
    max_audio_length_seconds: number;
    max_text_length: number;
    concurrent_jobs: number;
  };
  providers: string[];
  output_formats: string[];
  features: Record<string, boolean>;
  pricing: {
    monthly: number;
    yearly: number;
    yearly_savings: number;
  };
}

interface Subscription {
  tier: {
    id: string;
    name: string;
    slug: string;
    monthly_characters: number;
    price_monthly: number;
    price_yearly: number;
  };
  status: string;
  current_period_end: string | null;
  has_subscription: boolean;
}

interface UsageSummary {
  used: number;
  limit: number;
  percentage_used: number;
}

const FEATURE_LABELS: Record<string, string> = {
  api_access: 'API access',
  webhooks: 'Webhooks',
  priority_support: 'Priority support',
  priority_processing: 'Priority processing',
};

export function Billing() {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [loading, setLoading] = useState(true);
  const [upgradingTierId, setUpgradingTierId] = useState<string | null>(null);
  const [managingBilling, setManagingBilling] = useState(false);
  const [billingError, setBillingError] = useState('');

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    setLoading(true);
    const [subResult, tiersResult, usageResult] = await Promise.all([
      api.getSubscription(),
      api.getTiers(),
      api.getUsage(),
    ]);

    if (subResult.success && subResult.data) {
      setSubscription(subResult.data);
    }

    if (tiersResult.success && tiersResult.data) {
      setTiers(tiersResult.data);
    }

    if (usageResult.success && usageResult.data) {
      setUsage(usageResult.data.characters);
    }
    setLoading(false);
  };

  const handleUpgrade = async (tierId: string) => {
    if (upgradingTierId) return;
    setBillingError('');
    setUpgradingTierId(tierId);
    const result = await api.createCheckout(tierId, billingPeriod);
    if (result.success && result.data) {
      window.location.href = result.data.checkout_url;
      return; // keep the button spinning while the browser navigates away
    }
    setBillingError(result.error?.message || "Couldn't start checkout. Please try again.");
    setUpgradingTierId(null);
  };

  const handleManageSubscription = async () => {
    if (managingBilling) return;
    setBillingError('');
    setManagingBilling(true);
    const result = await api.createPortalSession();
    if (result.success && result.data) {
      window.location.href = result.data.portal_url;
      return;
    }
    setBillingError(result.error?.message || "Couldn't open the billing portal. Please try again.");
    setManagingBilling(false);
  };

  const formatCompact = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toLocaleString();
  };

  const formatRenewal = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const tierFeatures = (tier: Tier) => {
    const feats = [
      `${tier.limits.monthly_characters.toLocaleString()} chars / month`,
      `${tier.providers.join(' + ')} voices`,
      `${tier.output_formats.map(f => f.toUpperCase()).join(' & ')} export`,
      `${tier.limits.concurrent_jobs.toLocaleString()} concurrent job${tier.limits.concurrent_jobs > 1 ? 's' : ''}`,
    ];
    for (const [key, enabled] of Object.entries(tier.features)) {
      if (enabled) feats.push(FEATURE_LABELS[key] || key.replace(/_/g, ' '));
    }
    return feats;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={34} />
      </div>
    );
  }

  const usagePct = usage ? Math.min(100, Math.round(usage.percentage_used)) : null;

  return (
    <div className="fx max-w-[1080px] flex flex-col gap-[22px]">
      {/* Billing error */}
      {billingError && (
        <div
          className="p-4 rounded-xl text-sm"
          style={{ background: 'rgba(216, 54, 47, .12)', color: '#F0928C' }}
        >
          {billingError}
        </div>
      )}

      {/* Current plan summary */}
      {subscription && (
        <div
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 px-[26px] py-[22px] rounded-2xl border border-ink-700"
          style={{ background: 'linear-gradient(120deg, rgba(var(--a-rgb), .09), rgba(23,23,27,0))' }}
        >
          <div>
            <p className="m-0 text-mist-500 text-xs font-semibold uppercase tracking-[0.14em]">Current plan</p>
            <p className="mt-[6px] m-0 font-display font-extrabold text-white text-[26px] leading-tight">
              {subscription.tier.name}{' '}
              <span className="text-[16px]" style={{ color: 'var(--a-hi)' }}>
                · ${subscription.tier.price_monthly.toLocaleString()}/mo
              </span>
            </p>
            <p className="mt-1 m-0 text-mist-300 text-[13.5px]">
              {subscription.current_period_end
                ? `Renews ${formatRenewal(subscription.current_period_end)} · `
                : ''}
              {usage
                ? `${formatCompact(usage.used)} of ${formatCompact(usage.limit)} characters used`
                : `${formatCompact(subscription.tier.monthly_characters)} characters / month`}
            </p>
            {!!subscription.has_subscription && (
              <button
                type="button"
                onClick={handleManageSubscription}
                disabled={managingBilling}
                className="btn-ghost mt-4 disabled:opacity-50"
              >
                {managingBilling ? 'Opening…' : 'Manage billing'}
              </button>
            )}
          </div>
          {usagePct !== null && (
            <div className="md:text-right">
              <div className="w-[220px] h-2 rounded-full bg-ink-700 overflow-hidden mb-2 md:ml-auto">
                <div className="h-full grad-bar" style={{ width: `${usagePct}%` }} />
              </div>
              <span className="text-mist-500 text-[12.5px]">{usagePct}% used this cycle</span>
            </div>
          )}
        </div>
      )}

      {/* Billing period toggle */}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-1 rounded-full border border-ink-700 bg-ink-850 p-1">
          {(['monthly', 'yearly'] as const).map(period => (
            <button
              key={period}
              type="button"
              onClick={() => setBillingPeriod(period)}
              className={`px-4 py-[7px] rounded-full text-[12.5px] font-semibold transition-colors ${
                billingPeriod === period
                  ? 'grad-accent-simple text-ink-950'
                  : 'text-mist-400 hover:text-mist-200'
              }`}
            >
              {period === 'monthly' ? 'Monthly' : 'Yearly'}
              {period === 'yearly' && billingPeriod !== 'yearly' && (
                <span className="ml-1.5 text-[11px] font-normal" style={{ color: 'var(--a-hi)' }}>
                  Save up to 20%
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tier grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
        {tiers.map(tier => {
          const isCurrentPlan = subscription?.tier.id === tier.id;
          const price = billingPeriod === 'monthly' ? tier.pricing.monthly : tier.pricing.yearly / 12;

          return (
            <div
              key={tier.id}
              className="relative flex flex-col gap-[18px] p-[26px] rounded-2xl"
              style={
                isCurrentPlan
                  ? {
                      border: '1px solid var(--a-core)',
                      background: 'linear-gradient(180deg, rgba(var(--a-rgb), .09), rgba(23,23,27,.4))',
                      boxShadow: '0 14px 40px rgba(var(--a-rgb), .14)',
                    }
                  : { border: '1px solid #26262B', background: '#17171B' }
              }
            >
              <div>
                <p className="m-0 font-display font-extrabold text-white text-[18px] uppercase tracking-[0.05em]">
                  {tier.name}
                </p>
                <p className="mt-[10px] m-0 font-display font-extrabold text-white text-[38px] leading-none">
                  ${Math.round(price).toLocaleString()}
                  <span className="text-mist-500 text-[15px] font-semibold">/mo</span>
                </p>
                {billingPeriod === 'yearly' && (
                  <p className="mt-1 m-0 text-mist-500 text-[12px]">
                    ${tier.pricing.yearly.toLocaleString()}/year · save {tier.pricing.yearly_savings}%
                  </p>
                )}
                <p className="mt-1 m-0 text-[13px] font-semibold" style={{ color: 'var(--a-hi)' }}>
                  {formatCompact(tier.limits.monthly_characters)} characters / month
                </p>
              </div>

              <div className="flex flex-col gap-[11px]">
                {tierFeatures(tier).map((feat, i) => (
                  <div key={i} className="flex items-center gap-[10px] text-mist-300 text-[13.5px]">
                    <span className="flex flex-none" style={{ color: 'var(--a-core)' }}>
                      <Icon name="check" size={15} />
                    </span>
                    {feat}
                  </div>
                ))}
              </div>

              {isCurrentPlan ? (
                <button type="button" disabled className="btn-ghost w-full !cursor-default uppercase tracking-[0.05em] text-[12.5px]">
                  Current plan
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleUpgrade(tier.id)}
                  disabled={upgradingTierId === tier.id}
                  className="btn-grad w-full !text-[12.5px]"
                >
                  {upgradingTierId === tier.id ? 'Processing…' : `Choose ${tier.name}`}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
